import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff, faSync } from '@fortawesome/free-solid-svg-icons';
import { Loading } from './Loading';
import Paginator from './Paginator';
import { urlPod, endpointGetIngramTitles, endpointUpdateIngramStatus, endpointSendIngramRemovalNotice } from '../services/Endpoints';
import AuthService from '../services/AuthService';
import ConfirmationModal from './ConfirmationModal';


const ITEMS_PER_PAGE = 20;

const isIngramOn = (titleOrStatus) => {
    if (!titleOrStatus) return false;
    let val = titleOrStatus;
    if (typeof titleOrStatus === 'object' && titleOrStatus !== null) {
        if (titleOrStatus.status_ingram !== undefined) {
            val = titleOrStatus.status_ingram;
        }
        if (typeof val === 'object' && val !== null && val.status !== undefined) {
            val = val.status;
        }
    }
    if (val === true || val === 1 || val === '1') return true;
    if (typeof val === 'string') {
        const str = val.trim().toLowerCase();
        return str === 'on' || str === 'active' || str === '1' || str === 'true';
    }
    return false;
};


export function ContentIngram({ filters, refreshKey }) {
    const [loading, setLoading] = useState(false);
    const [titles, setTitles] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [togglingId, setTogglingId] = useState(null);
    const [sendingNoticeId, setSendingNoticeId] = useState(null);
    const [noticeSentIds, setNoticeSentIds] = useState([]);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [titleToToggle, setTitleToToggle] = useState(null);

    const search = filters?.search || '';
    const status = filters?.status || '';
    const account = filters?.account || '';


    const fetchTitles = useCallback(async (page = 1) => {
        console.log("fetchTitles");
        const token = AuthService.getToken();
        if (!token) return;
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page,
                limit: ITEMS_PER_PAGE,
                search,
                status,
                account,
            });
            const response = await fetch(`${urlPod}${endpointGetIngramTitles}?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token,
                    'Lang': 'es',
                },
            });
            const res = await response.json();
            if (res && res.status === 'success') {
                setTitles(res.data.records || []);
                setTotal(res.data.total || 0);
            }
        } catch (error) {
            console.error('Error fetching Ingram titles:', error);
        } finally {
            setLoading(false);
        }
    }, [refreshKey, search, status, account]);


    useEffect(() => {
        setCurrentPage(1);
        console.log("useEffect");
        fetchTitles(1);

        return () => setTitles([]);
    }, [refreshKey, search, status, account]);

    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
        fetchTitles(page);
    }, [fetchTitles]);

    const handleToggleStatus = (title) => {
        setTitleToToggle(title);
        setIsConfirmOpen(true);
    };

    const confirmToggleStatus = async () => {
        if (!titleToToggle) return;
        const title = titleToToggle;

        const token = AuthService.getToken();
        if (!token || String(togglingId) === String(title.ink_id)) {
            setIsConfirmOpen(false);
            return;
        }

        const newStatus = isIngramOn(title.status_ingram) ? 'off' : 'on';
        const today = new Date().toISOString().split('T')[0];
        setTogglingId(String(title.ink_id));
        try {
            const response = await fetch(`${urlPod}${endpointUpdateIngramStatus}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token,
                    'Lang': 'es',
                },
                body: JSON.stringify({ account: title.account, ink_id: title.ink_id, status: newStatus, date: today, title: title.title || '' }),

            });
            const res = await response.json();
            if (res && res.status === 'success') {
                setTitles(prev =>
                    prev.map(t =>
                        (String(t.ink_id) === String(title.ink_id) && t.account === title.account)
                            ? {
                                ...t,
                                status_ingram: { status: newStatus, date: today },
                                status_request: t.status_request ? { ...t.status_request, status: 'completed' } : null
                            }
                            : t
                    )
                );
            }
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setTogglingId(null);
            setIsConfirmOpen(false);
            setTitleToToggle(null);
        }
    };


    const handleSendRemovalNotice = async (title) => {
        const token = AuthService.getToken();
        if (!token || String(sendingNoticeId) === String(title.ink_id)) return;

        setSendingNoticeId(String(title.ink_id));
        try {
            const response = await fetch(`${urlPod}${endpointSendIngramRemovalNotice}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token,
                    'Lang': 'es',
                },
                body: JSON.stringify({
                    account: title.account,
                    ink_id: title.ink_id,
                    title: title.title || ''
                }),
            });
            const res = await response.json();
            if (res && res.status === 'success') {
                setNoticeSentIds(prev => [...prev, String(title.ink_id)]);
                setTitles(prev =>
                    prev.map(t =>
                        (String(t.ink_id) === String(title.ink_id) && t.account === title.account)
                            ? {
                                ...t,
                                status_request: {
                                    ...(t.status_request || {}),
                                    email_sent: true
                                }
                            }
                            : t
                    )
                );
                alert('Notificación de baja enviada a Ingram con éxito.');
            }
        } catch (error) {
            console.error('Error sending removal notice:', error);
        } finally {
            setSendingNoticeId(null);
        }
    };



    const getStatusBadge = (statusIngram) => {
        if (isIngramOn(statusIngram)) return <span className="badge-status status-1">ON</span>;
        if (statusIngram) return <span className="badge-status status-0">OFF</span>;
        return <span className="badge-status status-2">—</span>;
    };

    return (
        <>
            {loading && <Loading />}

            <div className="modern-table-container animate__animated animate__fadeIn">
                <table className="modern-table striped-table">
                    <thead>
                        <tr>
                            <th>Cuenta</th>
                            <th>Ink ID</th>
                            <th>Parent ISBN</th>
                            <th>ISBN / SKU</th>
                            <th>Título</th>
                            <th>Sello editorial</th>
                            <th>Colaboradores</th>
                            <th>Fecha estado</th>
                            <th>Acción</th>
                        </tr>
                    </thead>
                    <tbody>
                        {titles.length === 0 && !loading ? (
                            <tr>
                                <td colSpan="9" className="text-center py-5 text-white-50">
                                    <h6 className="m-0 opacity-50">No se encontraron resultados para los filtros aplicados</h6>
                                </td>
                            </tr>
                        ) : (
                            titles.map((title) => {
                                const isItemToggling = String(togglingId) === String(title.ink_id);
                                const isItemSending = String(sendingNoticeId) === String(title.ink_id);
                                const isNoticeSent = noticeSentIds.includes(String(title.ink_id)) || title.status_request?.email_sent === true || title.status_request?.email_sent === 1;
                                const dateDisplay = typeof title.status_ingram === 'object' && title.status_ingram?.date ? title.status_ingram.date : '—';

                                return (
                                    <tr key={`${title.account}-${title.ink_id}`}>
                                        <td className="short">{title.account || '—'}</td>
                                        <td className="short">{title.ink_id}</td>
                                        <td className="short">{title.parent_isbn || '—'}</td>
                                        <td className="short">{title.isbn_or_sku || '—'}</td>
                                        <td>{title.title}</td>
                                        <td>{title.imprint || '—'}</td>
                                        <td>{title.contributor || '—'}</td>
                                        <td className="short">{dateDisplay}</td>
                                        <td>
                                            <button
                                                className="btn btn-link p-0 d-flex align-items-center gap-2"
                                                style={{
                                                    color: isIngramOn(title.status_ingram) ? '#4ade80' : '#f87171',
                                                    fontSize: '1.4rem',
                                                    opacity: isItemToggling ? 0.5 : 1,
                                                    transition: 'color 0.2s, opacity 0.2s',
                                                }}
                                                onClick={() => handleToggleStatus(title)}
                                                disabled={isItemToggling}
                                                title={isIngramOn(title.status_ingram) ? 'Cambiar a OFF' : 'Cambiar a ON'}
                                            >
                                                <FontAwesomeIcon
                                                    icon={isIngramOn(title.status_ingram) ? faToggleOn : faToggleOff}
                                                    spin={isItemToggling}
                                                />
                                                <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                                    {isIngramOn(title.status_ingram) ? 'ON' : 'OFF'}
                                                </span>
                                            </button>

                                            {title.status_request?.status === 'pending' && title.status_request?.requested_status === 'off' && (
                                                <div className="mt-2 d-flex flex-column align-items-start gap-1">
                                                    <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>
                                                        Pendiente OFF
                                                    </span>
                                                    <button
                                                        className="btn btn-warning btn-sm mt-1 shadow-sm"
                                                        style={{ fontSize: '0.75rem', fontWeight: 600, padding: '4px 8px' }}
                                                        onClick={() => handleSendRemovalNotice(title)}
                                                        disabled={isItemSending || isNoticeSent}
                                                    >
                                                        {isItemSending
                                                            ? 'Enviando...'
                                                            : isNoticeSent
                                                                ? '✓ Notificación enviada'
                                                                : 'Enviar notificación de baja Ingram'}
                                                    </button>
                                                </div>
                                            )}

                                            {!isIngramOn(title.status_ingram) && title.status_request?.status === 'completed' && (
                                                <div className="mt-2">
                                                    <span
                                                        className="badge"
                                                        style={{
                                                            fontSize: '0.7rem',
                                                            background: '#9e9e9e',
                                                            color: 'var(--ink-text)',
                                                            padding: '4px 8px',
                                                            borderRadius: '6px',
                                                            fontWeight: 600,
                                                            letterSpacing: '0.03em',
                                                        }}
                                                        title="Este título fue dado de baja por solicitud"
                                                    >
                                                        Por solicitud
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {total > ITEMS_PER_PAGE && (
                <Paginator
                    totalItems={total}
                    itemsPerPage={ITEMS_PER_PAGE}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                />
            )}

            <ConfirmationModal
                isOpen={isConfirmOpen}
                onClose={() => {
                    setIsConfirmOpen(false);
                    setTitleToToggle(null);
                }}
                onConfirm={confirmToggleStatus}
                title="Cambiar estado en Ingram"
                message={`¿Estás seguro de cambiar el estado de este título (${titleToToggle?.title || titleToToggle?.isbn_or_sku}) a ${isIngramOn(titleToToggle?.status_ingram) ? 'OFF' : 'ON'}?`}
                confirmText="Sí, cambiar estado"
                cancelText="Cancelar"
                type="warning"
                isLoading={String(togglingId) === String(titleToToggle?.ink_id)}
            />
        </>
    );
}

export default ContentIngram;
